export interface NaturalStickyOptions {
    hideThreshold?: number;
    showThreshold?: number;
    autoSnap?: boolean;
    snapTimeout?: number;
    snapDuration?: number;
    snapEasing?: string;
}

export class NaturalMiddleSticky {
    private element: HTMLElement;
    private hideThreshold: number;
    private showThreshold: number;
    private autoSnap: boolean;
    private snapTimeout: number;
    private snapDuration: number;
    private snapEasing: string;

    private height: number = 0;
    private naturalTop: number = 0;
    private stickyOffset: number = 0;
    private effectiveStickPoint: number = 0;
    private edge: 'top' | 'bottom' = 'top';
    private currentTranslateY: number = 0;
    private lastScrollY: number;

    private origTransition: string = '';
    private origPos: string = '';
    private origTransform: string = '';

    private snapTimer: ReturnType<typeof setTimeout> | null = null;
    private snapEndTimer: ReturnType<typeof setTimeout> | null = null;
    private isSnapping: boolean = false;

    constructor(element: HTMLElement, options: NaturalStickyOptions = {}) {
        this.element = element;

        // Configurable options
        this.hideThreshold = options.hideThreshold || 0;
        this.showThreshold = options.showThreshold || 0;
        this.autoSnap = options.autoSnap ?? false;
        this.snapTimeout = options.snapTimeout ?? 500;
        this.snapDuration = options.snapDuration ?? 300;
        this.snapEasing = options.snapEasing || 'ease-out';

        this.lastScrollY = Math.max(0, window.scrollY);

        // Capture original inline styles ONLY ONCE to avoid corruption
        this.origPos = this.element.style.position || '';
        this.origTransform = this.element.style.transform || '';
        this.origTransition = this.element.style.transition || '';

        // Bind methods
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);

        // Initial measurement
        this.init();

        // ui:resize: viewport 変化（window.resize/load の代替）
        document.addEventListener('ui:resize', this.onResize);
        window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    destroy(): void {
        if (this.snapTimer !== null) {
            clearTimeout(this.snapTimer);
            this.snapTimer = null;
        }
        if (this.snapEndTimer !== null) {
            clearTimeout(this.snapEndTimer);
            this.snapEndTimer = null;
        }
        document.removeEventListener('ui:resize', this.onResize);
        window.removeEventListener('scroll', this.onScroll);
    }

    private init(): void {
        // Disable transition to prevent animation during measurement
        this.element.style.transition = 'none';

        // Temporarily make element static to read its natural document position
        this.element.style.position = 'static';
        this.element.style.transform = 'none';

        // Force layout and read measurements
        const rect = this.element.getBoundingClientRect();
        this.height = rect.height;

        // Calculate the natural top of the element relative to the document
        this.naturalTop = rect.top + window.scrollY;

        // Restore original position
        this.element.style.position = this.origPos;

        // Determine the edge from the data-sticky attribute
        const stickyAttr = this.element.getAttribute('data-sticky');
        this.edge = (stickyAttr === 'bottom') ? 'bottom' : 'top';

        const computedStyle = window.getComputedStyle(this.element);

        if (this.edge === 'top') {
            this.stickyOffset = parseFloat(computedStyle.top) || 0;
            // Stuck when scrollY goes past this point
            this.effectiveStickPoint = this.naturalTop - this.stickyOffset;
        } else {
            this.stickyOffset = parseFloat(computedStyle.bottom) || 0;
            // For bottom, stuck when scrollY is LESS than this point
            this.effectiveStickPoint = (this.naturalTop + this.height) + this.stickyOffset - window.innerHeight;
        }

        // Restore other original styles
        this.element.style.transform = this.origTransform;

        // Restore transition
        requestAnimationFrame(() => {
            this.element.style.transition = this.origTransition;
            this.onScroll();
        });
    }

    private onResize(): void {
        this.init();
    }

    private snapToVisible(): void {
        this.isSnapping = true;
        const transRule = `transform ${this.snapDuration}ms ${this.snapEasing}`;
        this.element.style.transition = this.origTransition
            ? `${transRule}, ${this.origTransition}`
            : transRule;

        // Force reflow to ensure the browser applies the transition before transforming
        void this.element.offsetHeight;

        requestAnimationFrame(() => {
            if (!this.isSnapping) return; // User scrolled in the same frame

            this.currentTranslateY = 0;
            this.element.style.transform = `translateY(0px)`;

            if (this.snapEndTimer !== null) {
                clearTimeout(this.snapEndTimer);
            }

            this.snapEndTimer = setTimeout(() => {
                this.isSnapping = false;
                this.element.style.transition = this.origTransition;
            }, this.snapDuration);
        });
    }

    private onScroll(): void {
        const currentScrollY = Math.max(0, window.scrollY);
        const delta = currentScrollY - this.lastScrollY;
        this.lastScrollY = currentScrollY;

        // Clear any existing snap timeout
        if (this.snapTimer !== null) {
            clearTimeout(this.snapTimer);
            this.snapTimer = null;
        }

        // If currently animating and user scrolled again, gracefully interrupt
        if (this.isSnapping) {
            const computedStyle = window.getComputedStyle(this.element);
            const transform = computedStyle.transform;
            if (transform !== 'none') {
                // Parse matrix(1, 0, 0, 1, tx, ty)
                const match = transform.match(/matrix\([^,]+, [^,]+, [^,]+, [^,]+, [^,]+, ([^)]+)\)/);
                if (match) {
                    this.currentTranslateY = parseFloat(match[1]);
                }
            }
            // Strip transition to let native scroll take over seamlessly
            this.isSnapping = false;
            this.element.style.transition = this.origTransition;
            if (this.snapEndTimer !== null) {
                clearTimeout(this.snapEndTimer);
                this.snapEndTimer = null;
            }
        }

        let isStuck = false;

        if (this.edge === 'top') {
            isStuck = currentScrollY > this.effectiveStickPoint;
            if (isStuck) {
                let activeDelta = delta;

                // Just entered sticky region from above (scrolling down)
                if (delta > 0 && (currentScrollY - delta) < this.effectiveStickPoint) {
                    activeDelta = currentScrollY - this.effectiveStickPoint;
                }

                const hideAmount = this.height + this.stickyOffset;

                // scroll down -> move UP (negative Y)
                this.currentTranslateY -= activeDelta;
                this.currentTranslateY = Math.max(-hideAmount, Math.min(0, this.currentTranslateY));

            } else {
                this.currentTranslateY = 0;
            }
        } else if (this.edge === 'bottom') {
            isStuck = currentScrollY < this.effectiveStickPoint;
            if (isStuck) {
                let activeDelta = delta;

                // Just entered sticky region from below (scrolling up)
                if (delta < 0 && (currentScrollY - delta) > this.effectiveStickPoint) {
                    activeDelta = currentScrollY - this.effectiveStickPoint;
                }

                const hideAmount = this.height + this.stickyOffset;

                // scroll down -> move DOWN to hide (positive Y)
                this.currentTranslateY += activeDelta;
                this.currentTranslateY = Math.max(0, Math.min(hideAmount, this.currentTranslateY));

            } else {
                this.currentTranslateY = 0;
            }
        }

        // Apply the visual translation
        this.element.style.transform = `translateY(${this.currentTranslateY}px)`;

        // Handle Auto-Snapping
        if (this.autoSnap && isStuck) {
            const hideAmount = this.height + this.stickyOffset;
            let isPartiallyVisible = false;

            if (this.edge === 'top') {
                // Not perfectly 0 and not perfectly hidden
                isPartiallyVisible = this.currentTranslateY < 0 && this.currentTranslateY > -hideAmount;
            } else {
                // Not perfectly 0 and not perfectly hidden
                isPartiallyVisible = this.currentTranslateY > 0 && this.currentTranslateY < hideAmount;
            }

            if (isPartiallyVisible) {
                this.snapTimer = setTimeout(() => {
                    this.snapToVisible();
                }, this.snapTimeout);
            }
        }
    }
}
